import { useDjango } from '@/composables/useDjango'
import { useGIDataUploadCollectionStore } from '@/stores/giDataUploadCollection'
import { useGIPointsStore } from '@/stores/giPoints'
import { useTaskPolling, type TaskProgress } from '@haskoning/vue-speckle'
import API from '@/api/apiUrls'

const { api } = useDjango()

export type ImportProgress = TaskProgress

/** `{ errorCode: [locationId, ...] }` — grouped by the backend, not truncated. */
export type ImportErrors = Record<string, string[]>

export type ImportGIDataResult = Record<string, any> & { errors: ImportErrors }

export function useGIDataSpeckleImport() {
  const giDataUploadCollectionStore = useGIDataUploadCollectionStore()
  const giPointStore = useGIPointsStore()

  const {
    progress: importProgress,
    start: startPolling,
    stop: stopPolling
  } = useTaskPolling((taskId) =>
    api.service(API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.TASK_STATUS(taskId)).get()
  )

  function applyImportedCollection(payload: any) {
    giDataUploadCollectionStore.addItems([payload])
    giPointStore.updateItemsFromResponse(payload)
  }

  async function importGIData(data: Record<string, any>): Promise<ImportGIDataResult> {
    importProgress.value = { percent: null, message: '', data: {} }

    const response = await api
      .service(API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.IMPORT_FROM_SPECKLE)
      .create(data)

    if (!response?.task_id) {
      applyImportedCollection(response)
      return { ...response, errors: response?.errors ?? {} }
    }

    try {
      const collection = await startPolling(response.task_id)
      applyImportedCollection(collection)
      return { ...collection, errors: collection?.errors ?? {} }
    } finally {
      stopPolling()
    }
  }

  return { importGIData, importProgress }
}
